import { useState } from 'react';
import { toast } from 'sonner';
import { SquarePen } from 'lucide-react';
import { useMutation } from '@apollo/client/react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { type IBillingAddress } from 'src/graphql/schema';
import { UpdateBillingAddressDocument } from 'src/graphql/actions/profile/updateBillingAddress.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { updateBillingAddressCache } from 'src/graphql/client/cache';

import BillingForm, { type IBillingForm } from './BillingForm';

type Props = {
  data?: IBillingAddress | null;
};

const EditDetailsDialog = ({ data }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [updateBillingAddress] = useMutation(UpdateBillingAddressDocument);

  const onSubmit = async (values: IBillingForm) => {
    try {
      const { data } = await updateBillingAddress({
        variables: {
          billingAddress: {
            firstName: values.firstName,
            lastName: values.lastName,
            companyName: values.company,
            addressLine1: values.address1,
            city: values.city,
            zip: values.zip,
            state: values.state,
            country: values.country,
            email: values.email,
            companyRegistrationNumber: values.companyRegistrationNumber || '',
          },
        },
      });

      if (data?.updateBillingAddress?.success) {
        toast.success('Billing information updated!');
        setIsOpen(false);
        updateBillingAddressCache(data.updateBillingAddress.billingAddress || {});
      } else {
        throw new ClientError(data?.updateBillingAddress?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <SquarePen />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[976px] gap-6">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">Edit details</DialogTitle>
          <DialogDescription className="text-xs">
            Enter the billing details of the organization. This includes the registered name and address of the entity,
            and the contact that will receive any invoices on behalf of your organization.
          </DialogDescription>
        </DialogHeader>
        <BillingForm data={data} onSubmit={onSubmit} onCancel={onCancel} />
      </DialogContent>
    </Dialog>
  );
};

export default EditDetailsDialog;
